/**
 * Stale-client detection helpers.
 *
 * The running bundle knows its own version via `process.env.APP_VERSION`
 * (baked at Next.js build time). The currently *deployed* version is read
 * from `/version.txt`, a static file written during the Docker build from
 * the same value. When the two differ, this tab is running an old bundle
 * and will eventually crash on a chunk the server no longer has.
 */

/** Session-storage key remembering which deployed version this tab already force-reloaded for. */
export const RELOADED_FOR_KEY = 'powston_reloaded_for_version';

/**
 * Stable Bugsnag groupingHash for stale-cache/chunk errors. Chunk filenames
 * are content-hashed, so without this every deploy mints a fresh Bugsnag
 * error group ("Failed to load chunk /_next/static/chunks/<hash>.js") that
 * has to be re-ignored by hand.
 */
export const STALE_CACHE_GROUPING_HASH = 'stale-chunk-or-cache';

/**
 * Try to detect if the error is from a stale cached page/chunk.
 * Common symptoms: ChunkLoadError, missing module, hydration mismatch from old HTML.
 */
export function looksLikeCacheError(error?: { name?: string; message?: string }): boolean {
  if (!error) return false;
  const msg = error.message || '';
  const name = error.name || '';
  return (
    name === 'ChunkLoadError' ||
    /loading chunk/i.test(msg) ||
    /loading css chunk/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /cannot find module/i.test(msg) ||
    /hydration/i.test(msg) ||
    /mismatch/i.test(msg)
  );
}

/**
 * Given the `errors` array of a Bugsnag event (each entry has `errorClass`
 * and `errorMessage`), return the stable stale-cache groupingHash when any
 * of them looks like a chunk/cache failure, otherwise undefined.
 *
 * Used by the central `onError` in BugsnagBoundary so that render/chunk-load
 * failures auto-reported by @bugsnag/plugin-react's ErrorBoundary collapse
 * into the same group as the manual route-level reports — otherwise those
 * automatic reports keep default (per-deploy, content-hashed) grouping.
 */
export function cacheGroupingHashFor(
  errors?: ReadonlyArray<{ errorClass?: string; errorMessage?: string }>,
): string | undefined {
  if (!errors || errors.length === 0) return undefined;
  const isCacheError = errors.some((e) =>
    looksLikeCacheError({ name: e.errorClass, message: e.errorMessage }),
  );
  return isCacheError ? STALE_CACHE_GROUPING_HASH : undefined;
}

/**
 * Parse the body of /version.txt. Returns null unless it looks like a
 * plausible build id (short git SHA or tag) — a CDN or proxy mis-serving
 * an HTML error page must not be mistaken for a version.
 */
export function parseVersionText(text: string | null | undefined): string | null {
  const v = text?.trim();
  if (!v) return null;
  if (v.length > 64 || /[<>\s]/.test(v)) return null;
  return v;
}

/**
 * Is this client running an older bundle than the server is deploying?
 * An empty running version means APP_VERSION wasn't provided at build
 * time (dev, or a misconfigured build) — staleness can't be judged, so
 * never report stale rather than reload every client forever.
 */
export function isStale(runningVersion: string | null | undefined, deployedVersion: string | null): boolean {
  if (!runningVersion || !deployedVersion) return false;
  return runningVersion !== deployedVersion;
}

/**
 * Should the client auto-reload for this deployed version? False once a
 * reload for the same version has been attempted, so a cached document
 * that still serves the old bundle can't cause a reload loop.
 */
export function shouldAutoReload(deployedVersion: string, previouslyReloadedFor: string | null): boolean {
  return previouslyReloadedFor !== deployedVersion;
}
